import React, { useState, useRef, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, isToday, addMonths, subMonths, startOfWeek, endOfWeek, isValid, parse } from 'date-fns';
import { cn } from "../../utils/cn";
import Input from './Input';
import Button from './Button';
import { enGB, fr } from 'date-fns/locale';
import { useLanguage } from '@/contexts/LanguageContext.jsx';
import PreviousIcon from '@/icons/afrimed/navigate_before.svg';
import NextIcon from '@/icons/afrimed/navigate_next.svg';
import AppIcon from "@/components/AppIcon.jsx";

const DatePicker = React.forwardRef(({
  value,
  onChange,
  placeholder = "Select date...",
  label,
  error,
  disabled = false,
  required = false,
  minDate,
  maxDate,
  className,
  fieldVariant = "default",
  popoverAlign = "start",
  popoverPlacement = "bottom",
  popoverClassName,
  calendarButtonClassName,
  calendarIconSrc,
  viewDate,
  dateFormat = "yyyy-MM-dd",
  displayFormat = "MMM d, yyyy",
  ...props
}, ref) => {
  const { languageCode } = useLanguage();
  const isFrench = String(languageCode).startsWith('fr');
  const locale = isFrench ? fr : enGB;
  const calendarIcon = calendarIconSrc ? <img src={calendarIconSrc} alt="" className="h-5 w-5" draggable="false" /> : <AppIcon name="Calendar" size={20} className="text-current" />;
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(value || viewDate || new Date());
  const [inputValue, setInputValue] = useState(
    value ? format(value, displayFormat, {locale}) : ""
  );
  
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Handle clicks outside to close calendar
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef?.current && !containerRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update input value when value prop changes
  useEffect(() => {
    setInputValue(value ? format(value, displayFormat, {locale}) : "");
  }, [value, displayFormat, locale]);

  useEffect(() => {
    const nextViewDate =
      viewDate instanceof Date && !Number.isNaN(viewDate.getTime()) ? viewDate : null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      setCurrentMonth(value);
      return;
    }
    if (nextViewDate) {
      setCurrentMonth(nextViewDate);
    }
  }, [value, viewDate]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const weekdays = isFrench ? ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const handleDateSelect = (date) => {
    if (disabled) return;
    
    // Check if date is within allowed range
    if (minDate && date < minDate) return;
    if (maxDate && date > maxDate) return;
    
    onChange?.(date);
    setInputValue(format(date, displayFormat, {locale}));
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    const newValue = e?.target?.value;
    setInputValue(newValue);
    
    // Try to parse the input value
    if (newValue) {
      const parsedDate = parse(newValue, displayFormat, new Date(), {locale});
      if (isValid(parsedDate)) {
        onChange?.(parsedDate);
        setCurrentMonth(parsedDate);
      }
    } else {
      onChange?.(null);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e?.key === 'Enter' || e?.key === ' ') {
      e?.preventDefault();
      setIsOpen(!isOpen);
    } else if (e?.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(prev => subMonths(prev, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(prev => addMonths(prev, 1));
  };

  const isDateDisabled = (date) => {
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  const popoverPositionClass = cn(
    popoverPlacement === "top" ? "bottom-full mb-2" : "top-full mt-2",
    popoverAlign === "end" ? "right-0" : "left-0"
  );

  const renderTrigger = () => {
    if (fieldVariant !== "m3") {
      return (
        <>
          <Input
            ref={inputRef}
            label={label}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onClick={() => setIsOpen(!isOpen)}
            placeholder={placeholder}
            error={error}
            disabled={disabled}
            required={required}
            className={cn("cursor-pointer bg-afrimed-surface-container-lowest border-afrimed-outline text-afrimed-on-surface", className)}
            {...props}
          />
          <Button
            type="button"
            variant="text"
            size="icon"
            className={cn(
              "absolute right-3 top-1/2 h-6 w-6 -translate-y-1/2 text-afrimed-on-surface-variant hover:bg-afrimed-state-surface-08",
              calendarButtonClassName
            )}
            onClick={() => setIsOpen(!isOpen)}
            disabled={disabled}
            aria-label={label || (isFrench ? "Choisir une date" : "Choose date")}
          >{calendarIcon}</Button>
        </>
      );
    }

    return (
      <button
        ref={inputRef}
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        onKeyDown={handleInputKeyDown}
        disabled={disabled}
        className={cn(
          "relative flex h-14 w-full items-center gap-3 rounded-[12px] border bg-[rgb(var(--clr-surface))] px-4 text-left",
          "shadow-[0px_0px_0px_1px_rgba(24,50,119,0.04),0px_1.5px_0px_-0.5px_rgba(26,27,35,0.06)]",
          "transition focus:outline-none focus:ring-2 focus:ring-[rgba(29,79,215,0.16)]",
          isOpen
            ? "border-[rgb(var(--afrimed-primary))]"
            : "border-[rgba(var(--clr-outline-variant),0.88)] hover:border-[rgba(var(--afrimed-primary),0.38)]",
          disabled && "cursor-not-allowed opacity-60",
          className
        )}
        aria-label={label || placeholder}
        aria-expanded={isOpen}
        {...props}
      >
        <span className="flex min-w-0 flex-1 flex-col justify-center">
          {label ? (
            <span className="truncate text-body-small text-primary">
              {label}
            </span>
          ) : null}
          <span
            className={cn(
              "min-w-0 truncate text-body-large",
              inputValue ? "text-on-surface" : "text-on-surface-var"
            )}
          >
            {inputValue || placeholder}
          </span>
        </span>
        <span
          className={cn(
            "grid h-10 w-10 shrink-0 place-items-center rounded-[12px] text-on-surface",
            isOpen && "text-primary"
          )}
          aria-hidden="true"
        >
          {calendarIcon}
        </span>
      </button>
    );
  };

  return (
    <div className="relative" ref={containerRef}>
      {renderTrigger()}
      {isOpen && !disabled && (
        <div
          className={cn(
            "absolute z-[70] w-[328px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-[12px]",
            "border border-[rgba(15,23,42,0.08)] bg-[rgb(var(--clr-surface-cont-highest))]",
            "shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.10),0px_6px_12px_-4px_rgba(26,27,35,0.08)]",
            popoverPositionClass,
            popoverClassName
          )}
        >
          {/* Calendar Header - M3 Style */}
          <div className="flex items-center justify-between border-b border-[rgba(var(--clr-outline-variant),0.72)] p-3">
            <Button
              type="button"
              variant="text"
              size="icon"
              onClick={goToPreviousMonth}
              aria-label={isFrench ? "Mois précédent" : "Previous month"}
              className="h-10 w-10 rounded-[12px] text-on-surface hover:bg-[rgba(24,50,119,0.06)]"
            ><img src={PreviousIcon} alt="" className="h-6 w-6" /></Button>
            
            <h2 className="text-title-medium text-on-surface">
              {format(currentMonth, 'MMMM yyyy', {locale})}
            </h2>
            
            <Button
              type="button"
              variant="text"
              size="icon"
              onClick={goToNextMonth}
              aria-label={isFrench ? "Mois suivant" : "Next month"}
              className="h-10 w-10 rounded-[12px] text-on-surface hover:bg-[rgba(24,50,119,0.06)]"
            ><img src={NextIcon} alt="" className="h-6 w-6" /></Button>
          </div>

          {/* Calendar Grid - M3 Style */}
          <div className="p-3">
            {/* Weekday Headers */}
            <div className="mb-2 grid grid-cols-7 gap-1">
              {weekdays?.map(day => (
                <div
                  key={day}
                  className="flex h-8 items-center justify-center text-label-small text-on-surface-var"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days - M3 Material Design */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays?.map(date => {
                const isCurrentMonth = isSameMonth(date, currentMonth);
                const isSelected = value && isSameDay(date, value);
                const isTodayDate = isToday(date);
                const isDisabled = isDateDisabled(date);

                return (
                  <button
                    key={date?.toISOString()}
                    type="button"
                    onClick={() => handleDateSelect(date)}
                    disabled={isDisabled}
                    className={cn(
                      "grid h-10 w-10 place-items-center rounded-[10px] text-body-small transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[rgba(29,79,215,0.16)]",
                      {
                        // Base styles - M3 compliant
                        "text-on-surface hover:bg-[rgba(24,50,119,0.06)]": isCurrentMonth && !isSelected && !isDisabled,
                        "text-on-surface-var opacity-58": !isCurrentMonth && !isDisabled,
                        
                        // Selected state - M3 primary container style
                        "bg-[rgb(var(--afrimed-primary))] text-[rgb(var(--afrimed-on-primary))]": isSelected && !isDisabled,
                        
                        // Today indicator - M3 outline style with AfriMed colors
                        "ring-1 ring-[rgba(var(--afrimed-primary),0.42)]": isTodayDate && !isSelected && !isDisabled,
                        
                        // Disabled state - M3 compliant
                        "cursor-not-allowed text-on-surface-var opacity-40": isDisabled,
                        
                        // Hover states for interactive dates - M3 state layers
                        "hover:text-primary": isCurrentMonth && !isSelected && !isDisabled,
                      }
                    )}
                  >
                    {format(date, 'd')}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick Actions Footer - M3 Style */}
          <div className="flex items-center justify-between border-t border-[rgba(var(--clr-outline-variant),0.72)] p-3">
            <Button
              type="button"
              variant="text"
              size="sm"
              onClick={() => {
                const today = new Date();
                if (!isDateDisabled(today)) {
                  handleDateSelect(today);
                }
              }}
              className="rounded-[12px] text-primary hover:bg-[rgba(24,50,119,0.06)]"
            >
              {isFrench ? "Aujourd’hui" : "Today"}
            </Button>
            
            <Button
              type="button"
              variant="text"
              size="sm"
              onClick={() => {
                onChange?.(null);
                setInputValue("");
                setIsOpen(false);
              }}
              className="rounded-[12px] text-on-surface-var hover:bg-[rgba(24,50,119,0.06)] hover:text-on-surface"
            >
              {isFrench ? "Effacer" : "Clear"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});

DatePicker.displayName = "DatePicker";

export default DatePicker;
